'use client';

import * as React from 'react';
import {
  SquareXIcon as Grid,
  ClipboardListIcon as Clipboard,
  FileSignature,
  Shield,
  Cpu,
  FolderKanban,
  Receipt,
  CreditCard,
  BadgeCheck,
  Landmark,
  BarChart3,
  MessageSquare,
  Mail,
  Blocks,
  Users,
  Tags,
  Scale,
  Send,
  X,
} from 'lucide-react';

import { FeedbackModal } from '@/components/FeedbackModal/FeedbackModal';
import { DashboardSidebarCreateButton } from '@/components/dashboard/DashboardSidebarFooter';
import { NavMain } from '@/components/nav-main';
import { NavMonomoAi } from '@/components/nav-monomo-ai';
import { NavSecondary } from '@/components/nav-secondary';
import { NavUser } from '@/components/nav-user';
import { Notifications } from '@/components/Notifications';
import { AccountSwitcher } from '@/components/AccountSwitcher';
import { Logo } from '@/components/Logo';
import {
  SidebarDashboardIcon,
  SidebarInquiriesIcon,
  SidebarProjectsIcon,
  SidebarWalletIcon,
} from '@/components/sidebar/sidebar-icons';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  sidebarCollapseLabelClassName,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { profileApi } from '@/lib/api/profile';
import { captureFeedbackScreenshot } from '@/lib/feedbackScreenshot';
import {
  canViewAllProjects,
  canCreateInquiry,
  shouldShowInquiries,
  shouldShowProjects,
  isInquirerAccount,
  isPrince2Account,
} from '@/lib/permissions';
import { useQuery } from '@tanstack/react-query';
import { approvalsApi } from '@/lib/api/project-governance';
import { approvalsInboxQueryKey } from '@/components/project-governance/useGovernanceMutation';
import { AccountType, RoleOptions } from '@paladin-monorepo/common';

function shouldShowMonomoAiNav(user: Parameters<typeof buildMenuForUser>[0]): boolean {
  const isPlatformStaff =
    user?.role === RoleOptions.SUPERADMIN || user?.role === RoleOptions.SUPPORT;
  const isSupplierAccount =
    user?.account?.accountType === AccountType.Consultant ||
    user?.account?.accountType === AccountType.SolutionProvider;
  return !isPlatformStaff && (isInquirerAccount(user) || isSupplierAccount);
}

function buildMenuForUser(user: any, t: any, approvalsCount = 0) {
  let menuItems = [];

  const isPlatformStaff =
    user?.role === RoleOptions.SUPERADMIN || user?.role === RoleOptions.SUPPORT;

  if (!isPlatformStaff) {
    menuItems.push({
      title: t('dashboard.title'),
      url: '/',
      icon: SidebarDashboardIcon,
      items: [],
    });
  }

  // SUPERADMIN/SUPPORT: show "Projects" with "All Projects" submenu (no create option for SUPERADMIN)
  if (canViewAllProjects(user)) {
    menuItems.push({
      title: t('sidebar.inquiries.title'),
      url: '/inquiries',
      icon: SidebarInquiriesIcon,
      isActive: true,
      items: [
        {
          title: t('sidebar.inquiries.all'),
          url: '/inquiries',
        },
        // SUPERADMIN cannot create projects (no way to bind to account)
        ...(user.role !== RoleOptions.SUPERADMIN
          ? [
            {
              title: t('sidebar.inquiries.createNew'),
              url: '/inquiries/new',
            },
          ]
          : []),
      ],
    });
  }
  // ACCOUNT_ADMIN/EMPLOYEE + inquirer: show "Projects" with "All Projects" and "Create New"
  else if (isInquirerAccount(user)) {
    menuItems.push({
      title: t('sidebar.projects.title'),
      url: '/projects?tab=active',
      icon: SidebarProjectsIcon,
      isActive: false,
      items: [
        { title: t('sidebar.projects.concepts'), url: '/projects?tab=concepts' },
        { title: t('sidebar.projects.active'), url: '/projects?tab=active', main: true },
        { title: t('sidebar.projects.archive'), url: '/projects?tab=archive' },
      ],
    });

    menuItems.push({
      title: t('sidebar.inquiries.title'),
      url: '/inquiries',
      icon: SidebarInquiriesIcon,
      isActive: false,
      items: [
        {
          title: t('sidebar.inquiries.all'),
          url: '/inquiries',
        },
        ...(canCreateInquiry(user)
          ? [
            {
              title: t('sidebar.inquiries.createNew'),
              url: '/inquiries/new',
            },
          ]
          : []),
      ],
    });
  }
  // ACCOUNT_ADMIN/EMPLOYEE + consultant/solutionProvider: show "Inquiries" and "Projects"
  else if (shouldShowInquiries(user) || shouldShowProjects(user)) {
    menuItems.push({
      title: t('sidebar.inquiries.title'),
      url: '/inquiries',
      icon: SidebarInquiriesIcon,
      isActive: false,
      items: [
        {
          title: t('sidebar.inquiries.all'),
          url: '/inquiries',
        },
      ],
    });

    menuItems.push({
      title: t('sidebar.projects.title'),
      url: '/projects',
      icon: SidebarProjectsIcon,
      isActive: false,
      items: [
        { title: t('sidebar.projects.concepts'), url: '/projects?tab=concepts' },
        { title: t('sidebar.projects.active'), url: '/projects?tab=active', main: true },
        { title: t('sidebar.projects.archive'), url: '/projects?tab=archive' },
      ],
    });
  }

  // Supplier role holders see the item only when a request is open for them.
  if (!isPlatformStaff && (isPrince2Account(user) || approvalsCount > 0)) {
    menuItems.push({
      title: t('sidebar.approvals'),
      url: '/approvals',
      icon: BadgeCheck,
      badge: approvalsCount,
      items: [],
    });
  }

  if (
    !isPlatformStaff &&
    isPrince2Account(user) &&
    (user?.role === RoleOptions.ACCOUNT_OWNER || user?.role === RoleOptions.ACCOUNT_ADMIN)
  ) {
    menuItems.push({
      title: t('sidebar.costs'),
      url: '/costs',
      icon: Receipt,
      items: [],
    });
  }

  if (user?.role && (user.role === RoleOptions.ACCOUNT_OWNER || user.role === RoleOptions.ACCOUNT_ADMIN) && isInquirerAccount(user)) {
    menuItems.push({
      title: t('sidebar.payments'),
      url: '/payments',
      icon: SidebarWalletIcon,
      isActive: true,
      items: [],
    });
  }

  // Admin section (SUPERADMIN / SUPPORT): Accounts, Project Briefs, Sessions, DigiSign
  if (user?.role === RoleOptions.SUPERADMIN || user?.role === RoleOptions.SUPPORT) {
    menuItems = [
      {
        title: t('sidebar.admin.users'),
        icon: Users,
        url: '/admin/accounts',
        isActive: true,
        items: [
          {
            title: t('sidebar.admin.accounts'),
            url: '/admin/accounts',
            icon: Grid,
            isActive: true
          },
          {
            title: t('sidebar.admin.sessions'),
            url: '/admin/sessions',
            icon: Shield,
            isActive: true
          },
          {
            title: t('sidebar.admin.credentialReviews'),
            url: '/admin/credential-reviews',
            icon: BadgeCheck,
            isActive: true
          },
          {
            title: t('sidebar.admin.payoutBankAccounts'),
            url: '/admin/payout-bank-accounts',
            icon: Landmark,
            isActive: true
          },
          {
            title: t('sidebar.admin.observationTypes'),
            url: '/admin/observation-types',
            icon: Tags,
            isActive: true
          },
        ],
      },
      {
        title: t('sidebar.admin.projects'),
        icon: FolderKanban,
        url: '/admin/projects',
        isActive: true,
        items: [
          {
            title: t('sidebar.admin.inquiries'),
            url: '/admin/inquiries',
            icon: Clipboard,
            isActive: true,
          },
          {
            title: t('sidebar.admin.projects'),
            url: '/admin/projects',
            icon: FolderKanban,
            isActive: true,
            main: true,
          },
          {
            title: t('sidebar.admin.fees'),
            url: '/admin/fees',
            icon: Receipt,
            isActive: true,
          },
          {
            title: t('sidebar.admin.subscriptionPlans'),
            url: '/admin/subscription-plans',
            icon: CreditCard,
            isActive: true,
          },
          {
            title: t('sidebar.admin.disputes'),
            url: '/admin/disputes',
            icon: Scale,
            isActive: true,
          },
          {
            title: t('sidebar.admin.refunds'),
            url: '/admin/refund-requests',
            icon: Receipt,
            isActive: true,
          },
        ],
      },
      {
        title: t('sidebar.admin.integrations'),
        icon: Blocks,
        url: '#',
        items: [
          {
            title: t('sidebar.admin.digisign'),
            url: '/admin/digisign/envelopes',
            icon: FileSignature,
            isActive: true,
            items: [],
          },
          {
            title: t('sidebar.admin.smtp'),
            url: '/admin/smtp',
            icon: Mail,
            isActive: true,
            items: [],
          },
        ],
      },
      {
        title: t('sidebar.admin.ai'),
        icon: Cpu,
        url: '#',
        items: [
          {
            title: t('sidebar.admin.llmModels'),
            url: '/admin/llm-models',
            icon: Cpu,
            isActive: true,
            items: [],
          },
          {
            title: t('sidebar.admin.llmUsage'),
            url: '/admin/llm-usage',
            icon: BarChart3,
            isActive: true,
            items: [],
          },
          {
            title: t('sidebar.admin.llmChats'),
            url: '/admin/llm-chats',
            icon: MessageSquare,
            isActive: true,
            items: [],
          },
          {
            title: t('sidebar.admin.agentConfigs'),
            url: '/admin/agent-configs',
            icon: Cpu,
            isActive: true,
            items: [],
          },
        ],
      },
      {
        title: t('sidebar.admin.feedbackInbox'),
        url: '/admin/feedback',
        icon: MessageSquare,
        isActive: true,
      },
    ];
  }

  return menuItems;
}

function SidebarFeedbackButton({
  onClick,
  capturing,
}: {
  onClick: () => void;
  capturing: boolean;
}) {
  const { t } = useTranslation();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          type="button"
          size="sm"
          tooltip={t('sidebar.feedback')}
          disabled={capturing}
          onClick={onClick}
          className="text-white hover:bg-white/10 hover:text-white"
        >
          <Send className="size-4" />
          <span className={cn('flex-1', sidebarCollapseLabelClassName)}>
            {capturing ? t('sidebar.feedbackCapturing') : t('sidebar.feedback')}
          </span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation();
  const { isMobile, setOpenMobile } = useSidebar();
  const { user } = useAuth({
    middleware: 'auth',
    redirectTo: '/',
    tmp: 'AppSidebar',
  });
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [feedbackScreenshot, setFeedbackScreenshot] = React.useState<Blob | null>(null);
  const [feedbackCapturing, setFeedbackCapturing] = React.useState(false);

  const openFeedback = React.useCallback(async () => {
    setFeedbackCapturing(true);
    try {
      const blob = await captureFeedbackScreenshot();
      setFeedbackScreenshot(blob);
    } finally {
      setFeedbackCapturing(false);
      setFeedbackOpen(true);
    }
  }, []);

  const isPlatformStaff = user?.role === RoleOptions.SUPERADMIN || user?.role === RoleOptions.SUPPORT;
  const { data: approvalsInbox } = useQuery({
    queryKey: approvalsInboxQueryKey,
    queryFn: () => approvalsApi.inbox(),
    enabled: !!user?.account && !isPlatformStaff,
    staleTime: 60_000,
    retry: false,
  });

  const data = {
    user: {
      // name: user?.firstName + ' ' + user?.lastName,
      name: user?.email || null,
      email: user?.email || '',
      avatar: profileApi.getMyPhotoUrl(),
    },
    navMain: buildMenuForUser(user, t, approvalsInbox?.mine.length ?? 0),
    navSecondary: [
      // {
      //   title: "Support",
      //   url: "#",
      //   icon: LifeBuoy,
      // },
      // {
      //   title: "Feedback",
      //   url: "#",
      //   icon: Send,
      // },
    ],
    projects: [
      // {
      //   name: "Design Engineering",
      //   url: "#",
      //   icon: Frame,
      // },
      // {
      //   name: "Sales & Marketing",
      //   url: "#",
      //   icon: PieChart,
      // },
      // {
      //   name: "Travel",
      //   url: "#",
      //   icon: Map,
      // },
    ],
  };

  return (
    <>
    <Sidebar variant="inset" {...props} collapsible="icon">
        {isMobile ? (
          <>
            <SidebarHeader className="flex flex-row items-center justify-between gap-2 p-3 pb-2">
              <Logo
                noName={false}
                className="min-w-0 gap-2 px-0 [&_svg]:size-7 [&_svg]:fill-white [&_span]:font-display [&_span]:text-base [&_span]:text-white"
              />
              <button
                type="button"
                className="flex size-8 shrink-0 items-center justify-center rounded-md border border-white/50 text-white hover:bg-white/10"
                aria-label={t('sidebar.closeMenu')}
                onClick={() => setOpenMobile(false)}
              >
                <X className="size-4" />
              </button>
            </SidebarHeader>
            <SidebarContent className="gap-3 overflow-x-hidden px-1">
              <NavMain items={data.navMain} />
              {shouldShowMonomoAiNav(user) && <NavMonomoAi />}
              <DashboardSidebarCreateButton user={user} />
              <NavSecondary items={data.navSecondary} className="mt-auto" />
            </SidebarContent>
            <SidebarFooter className="gap-3 p-3 pt-2">
              <SidebarFeedbackButton onClick={() => void openFeedback()} capturing={feedbackCapturing} />
              <NavUser user={data.user} variant="mobileFooter" />
              <p className="font-label text-white/70">{t('sidebar.aiVersion')}</p>
            </SidebarFooter>
          </>
        ) : (
          <>
            <SidebarHeader className="flex flex-row items-center gap-2 p-3 pb-2 group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:gap-1.5 group-data-[collapsible=icon]:p-2">
              <div className="min-w-0 flex-1 group-data-[collapsible=icon]:flex-none">
                <NavUser user={data.user} />
              </div>
              <Notifications variant="sidebar" />
            </SidebarHeader>
            <SidebarContent className="gap-3 overflow-x-hidden">
              <NavMain items={data.navMain} />
              {shouldShowMonomoAiNav(user) && <NavMonomoAi />}
              <DashboardSidebarCreateButton user={user} />
              <NavSecondary items={data.navSecondary} className="mt-auto" />
            </SidebarContent>
            <SidebarFooter className="gap-3 p-3 pt-2 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:p-2">
              {(user?.memberships?.length ?? 0) > 1 && (
                <div className="group-data-[collapsible=icon]:hidden">
                  <AccountSwitcher
                    currentAccountId={user?.currentAccountId || user?.account?.id}
                    currentAccountName={user?.account?.party?.name}
                    honoraryFlag={user?.account?.honoraryFlag}
                    memberships={user?.memberships || []}
                  />
                </div>
              )}
              <SidebarFeedbackButton onClick={() => void openFeedback()} capturing={feedbackCapturing} />
              <Logo
                noName={false}
                className="px-1 [&_svg]:size-[28px] [&_svg]:fill-white [&_span]:font-display [&_span]:text-lg [&_span]:font-normal [&_span]:text-white group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:[&_span]:hidden"
              />
            </SidebarFooter>
          </>
        )}
      </Sidebar>
      <FeedbackModal
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        screenshotBlob={feedbackScreenshot}
      />
    </>
  );
}
